import React, { useState, useEffect, useCallback, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { parseCustomerShippingAddress } from '../../utils/customerShippingAddress';
import { CHECKOUT_COUNTRIES } from '../../constants/checkoutCountries';

const T = {
  ink: '#1f2937',
  inkLight: '#6b7280',
  border: 'rgba(17,24,39,0.08)',
  pink: '#ec4899',
  purple: '#8b5cf6',
  gradient: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
  green: '#15803d',
  greenPale: '#dcfce7',
  red: '#b91c1c',
  redPale: '#fee2e2',
  grayPale: '#f3f4f6',
};

const card = { background: '#fff', color: T.ink, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 18px', marginBottom: 16 };
const input = { padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const primaryBtn = { background: T.gradient, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const ghostBtn = { background: '#fff', color: T.ink, border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const optionBtn = (active) => ({
  ...ghostBtn,
  textAlign: 'left',
  width: '100%',
  marginBottom: 8,
  border: active ? `2px solid ${T.purple}` : `1px solid ${T.border}`,
  background: active ? '#f5f3ff' : '#fff',
});

/**
 * "Sat 4 Oct, 9:00 am – 1:00 pm @ Noosa Markets stall 12" in the viewer's local time — or just
 * "Sat 4 Oct" for a pickup whose place & time the shop only reveals by Instagram DM.
 */
const formatSession = (s) => {
  if (!s) return '';
  if (s.hidden) return s.day;
  const start = new Date(s.startsAt);
  const end = new Date(s.endsAt);
  const day = start.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = (d) => d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  const where = s.locationAddress ? `${s.locationName} (${s.locationAddress})` : s.locationName;
  return `${day}, ${time(start)} – ${time(end)} @ ${where}`;
};

const detailRow = (label, value) => (
  <div style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 4, color: T.ink }}>
    <span style={{ color: T.inkLight, minWidth: 78 }}>{label}</span>
    <span style={{ flex: 1 }}>{value}</span>
  </div>
);

/** Everything about one order line — shown when the customer opens it. */
const OrderDetails = ({ order: o }) => {
  const placed = new Date(o.createdAt).toLocaleString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
  const a = o.dropoffAddress || {};
  const address = [a.street, a.apartment, a.city, a.state, a.postcode].filter(Boolean).join(', ');

  let how;
  let status;
  if (o.deliveryMode === 'stall') {
    how = `🛍️ Bought at the stall${o.pickupSession ? ` — ${formatSession(o.pickupSession)}` : ''}`;
    status = o.fulfilledAt ? `✅ Handed over ${new Date(o.fulfilledAt).toLocaleDateString('en-AU')}` : '🛍️ Collect it at the stall now';
  } else if (o.deliveryMode === 'pickup') {
    how = `📍 Pickup — ${
      o.pickupSession
        ? `${formatSession(o.pickupSession)}${o.pickupSession.hidden ? `. ${IG_DETAILS_HINT}` : ''}`
        : "awaiting a pickup time (we'll let you know when & where)"
    }`;
    status = o.fulfilledAt
      ? `✅ Collected ${new Date(o.fulfilledAt).toLocaleDateString('en-AU')}`
      : o.customerArrivedAt
      ? "🙋 We know you're here — coming with your order"
      : '⏳ Waiting for you to collect';
  } else {
    how = `📬 Mailbox delivery${address ? ` to ${address}` : ''}`;
    status = o.fulfilledAt ? `✅ Delivered ${new Date(o.fulfilledAt).toLocaleDateString('en-AU')}` : '🚚 On its way this week';
  }

  return (
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: T.ink, marginBottom: 8 }}>Order details</div>
      {detailRow('Item', `${o.secretProduct.name} × ${o.quantity}`)}
      {detailRow('Paid', o.amount === 0 ? 'Free' : `$${o.amount.toFixed(2)}`)}
      {detailRow('Ordered', placed)}
      {detailRow('How', how)}
      {detailRow('Status', status)}
      {detailRow('Order #', o.id.slice(0, 8).toUpperCase())}
    </div>
  );
};

const IG_DETAILS_HINT = "Where & when comes by Instagram DM — comment on any of my posts.";

/** "Order by Wed 1 Oct, 9:00 am" — when pickup orders for a session close. */
const orderByText = (s) => {
  const closes = new Date(s.orderCutoffAt || s.startsAt);
  return `order by ${closes.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`;
};

const stepBtn = { width: 28, height: 28, borderRadius: 8, border: `1px solid ${T.border}`, background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: '1' };

let stripePromiseCache = null;
async function getStripePromise() {
  if (stripePromiseCache) return stripePromiseCache;
  try {
    const backendUrl = API_BASE_URL.replace(/\/api\/v1$/, '');
    const businessId = process.env.REACT_APP_BUSINESS_ID || '';
    const res = await fetch(`${backendUrl}/api/v1/business/stripe-public${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`);
    const data = await res.json().catch(() => ({}));
    const key = (data.publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
    stripePromiseCache = key ? loadStripe(key) : null;
  } catch {
    const fallback = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
    stripePromiseCache = fallback ? loadStripe(fallback) : null;
  }
  return stripePromiseCache;
}

/** Inner form — must render inside <Elements> so useStripe/useElements resolve. */
const PayForm = ({ onPaid, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    setSubmitting(false);
    if (confirmError) {
      setError(confirmError.message || 'Payment failed');
      return;
    }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      onPaid();
    }
  };

  return (
    <div>
      <PaymentElement />
      {error && <p style={{ color: T.red, fontSize: 13, marginTop: 10 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button type="button" style={primaryBtn} onClick={handlePay} disabled={submitting || !stripe}>
          {submitting ? 'Processing…' : 'Pay now'}
        </button>
        <button type="button" style={ghostBtn} onClick={onCancel} disabled={submitting}>Cancel</button>
      </div>
    </div>
  );
};

const SecretShopTab = ({ showAccessTabHint = true } = {}) => {
  const { getAuthHeaders, customer } = useCustomerAuth();
  const savedAddress = parseCustomerShippingAddress(customer?.shippingAddress);

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [minQualifyingPrice, setMinQualifyingPrice] = useState(100);
  const [dropoffSurcharge, setDropoffSurcharge] = useState(5);
  const [pickupFee, setPickupFee] = useState(10);
  const [pickupSessions, setPickupSessions] = useState([]);
  const [liveSession, setLiveSession] = useState(null); // a stall running now — buy on the spot
  const [livePickup, setLivePickup] = useState(null); // a pickup time running now — "I'm here"
  const [laterMode, setLaterMode] = useState('pickup'); // at the stall: how not-at-the-stall items get to them
  const [needsPhone, setNeedsPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [pickupBlockedByInstagram, setPickupBlockedByInstagram] = useState(false);
  const [passwordDraft, setPasswordDraft] = useState('');
  const [passwordConfirmDraft, setPasswordConfirmDraft] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [arriving, setArriving] = useState(false);
  const [arrivedNote, setArrivedNote] = useState('');
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState(300);
  const [discount5Threshold, setDiscount5Threshold] = useState(700);
  const [discount10Threshold, setDiscount10Threshold] = useState(1500);
  const [myOrders, setMyOrders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [openVideoIndex, setOpenVideoIndex] = useState(null);
  const videoScrollerRef = useRef(null);

  const [quantities, setQuantities] = useState({}); // { [productId]: number }
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState('dropoff'); // 'dropoff' | 'pickup' | 'stall'
  const [pickupSessionId, setPickupSessionId] = useState('');
  const [address, setAddress] = useState({ street: '', apartment: '', city: '', state: '', postcode: '', country: 'Australia' });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  const [messagingOrderId, setMessagingOrderId] = useState(null);
  const [orderMessages, setOrderMessages] = useState([]);
  const [loadingOrderMessages, setLoadingOrderMessages] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const secretShop = API_ENDPOINTS.secretShop;

  const loadAccess = useCallback(async () => {
    try {
      const res = await fetch(secretShop.myAccess, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data?.success) {
        setProducts(data.data.products || []);
        if (data.data.minQualifyingPrice) setMinQualifyingPrice(data.data.minQualifyingPrice);
        if (data.data.dropoffSurcharge != null) setDropoffSurcharge(data.data.dropoffSurcharge);
        if (data.data.pickupFee != null) setPickupFee(data.data.pickupFee);
        setPickupSessions(data.data.pickupSessions || []);
        setLiveSession(data.data.liveSession || null);
        setLivePickup(data.data.livePickup || null);
        setNeedsPhone(!!data.data.needsPhone);
        setNeedsPassword(!!data.data.needsPassword);
        setPickupBlockedByInstagram(!!data.data.pickupRequiresInstagram && !data.data.instagramLinked);
        if (data.data.freeDeliveryThreshold != null) setFreeDeliveryThreshold(data.data.freeDeliveryThreshold);
        if (data.data.discount5Threshold != null) setDiscount5Threshold(data.data.discount5Threshold);
        if (data.data.discount10Threshold != null) setDiscount10Threshold(data.data.discount10Threshold);
      }
    } catch {
      // Access check failing is non-fatal — the redeem box still works.
    }
  }, [getAuthHeaders, secretShop]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await fetch(secretShop.myOrders, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data?.success) setMyOrders(data.data || []);
    } catch {
      // Non-fatal.
    }
  }, [getAuthHeaders, secretShop]);

  const loadVideos = useCallback(async () => {
    try {
      const res = await fetch(secretShop.myVideos, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data?.success) setVideos(data.data || []);
    } catch {
      // Non-fatal.
    }
  }, [getAuthHeaders, secretShop]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAccess(), loadOrders(), loadVideos()]).finally(() => setLoading(false));
  }, [loadAccess, loadOrders, loadVideos]);

  useEffect(() => {
    if (openVideoIndex !== null && videoScrollerRef.current) {
      const el = videoScrollerRef.current;
      el.scrollLeft = openVideoIndex * el.clientWidth;
    }
  }, [openVideoIndex]);

  const hasQualifyingItemInCart = products.some(
    (p) => p.price >= minQualifyingPrice && (quantities[p.id] || 0) > 0
  );
  // Stall-only products (only sent while a session is scheduled/live) never need a $100 item alongside,
  // and at a live stall there's no $100 minimum at all, so everything shows.
  const visibleProducts = products.filter(
    (p) => p.stallOnly || p.price === 0 || p.price >= minQualifyingPrice || hasQualifyingItemInCart || !!liveSession
  );
  const cartItems = visibleProducts
    .filter((p) => (quantities[p.id] || 0) > 0)
    .map((p) => ({ product: p, quantity: quantities[p.id] }));
  const cartHasStallOnly = cartItems.some((i) => i.product.stallOnly);
  const cartNotAtStall = cartItems.filter((i) => i.product.notAtStall);
  const subtotal = cartItems.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  // Mirrors calculateSecretShopCart in backend/src/services/secret-shop.service.ts — keep in sync.
  const discountRate = subtotal >= discount10Threshold ? 0.1 : subtotal >= discount5Threshold ? 0.05 : 0;
  // At the stall, products not stocked there are split off and picked up / delivered later.
  const pickupAvailable = pickupSessions.length > 0 && !pickupBlockedByInstagram;
  const effectiveLaterMode = laterMode === 'pickup' && !pickupAvailable ? 'dropoff' : laterMode;
  const laterItems = deliveryMode === 'stall' ? cartNotAtStall : [];
  const nowItems = deliveryMode === 'stall' ? cartItems.filter((i) => !i.product.notAtStall) : [];
  // Where the delivered / picked-up part goes: the whole cart, or at the stall just the later items.
  const fulfilMode = deliveryMode === 'stall' ? (laterItems.length > 0 ? effectiveLaterMode : null) : deliveryMode;
  const fulfilItems = deliveryMode === 'stall' ? laterItems : cartItems;
  const fulfilSubtotal = fulfilItems.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  // A cart of only $0 products is a test order — no fee in any mode (matches the backend).
  let fee = 0;
  if (subtotal > 0 && fulfilMode === 'dropoff' && fulfilSubtotal < freeDeliveryThreshold) fee = dropoffSurcharge;
  if (subtotal > 0 && fulfilMode === 'pickup') fee = pickupFee;
  const total = Math.round(subtotal * (1 - discountRate) * 100) / 100 + fee;
  // Mirrors calculateSecretShopCart: the part that's picked up / delivered needs a $100 item unless it's
  // only stall-only products; what's handed over at the stall can be any amount.
  const needsQualifyingItem =
    subtotal > 0 &&
    fulfilItems.length > 0 &&
    !fulfilItems.every((i) => i.product.stallOnly) &&
    !fulfilItems.some((i) => i.product.price >= minQualifyingPrice);
  const selectedPickupSession = pickupSessions.find((s) => s.id === pickupSessionId) || null;
  // This customer's pickups waiting at the session running right now — what "I'm here" flags.
  const pickupsHereNow = livePickup
    ? myOrders.filter(
        (o) =>
          o.deliveryMode === 'pickup' &&
          o.pickupSession?.id === livePickup.id &&
          !o.fulfilledAt &&
          o.paymentStatus === 'paid'
      )
    : [];
  const alreadyArrived = pickupsHereNow.length > 0 && pickupsHereNow.every((o) => o.customerArrivedAt);

  const setQuantity = (productId, qty) => {
    setQuantities((prev) => ({ ...prev, [productId]: Math.max(0, Math.floor(qty) || 0) }));
  };

  const openCheckout = () => {
    setAddress(
      savedAddress
        ? {
            street: savedAddress.street,
            apartment: savedAddress.apartment,
            city: savedAddress.city,
            state: savedAddress.state,
            postcode: savedAddress.postalCode,
            country: CHECKOUT_COUNTRIES.find((c) => c.code === savedAddress.country)?.label || 'Australia',
          }
        : { street: '', apartment: '', city: '', state: '', postcode: '', country: 'Australia' }
    );
    // At a live stall, buying on the spot is the obvious default; stall-only items can't be delivered.
    setDeliveryMode(
      liveSession ? 'stall' : cartHasStallOnly && pickupSessions.length > 0 && !pickupBlockedByInstagram ? 'pickup' : 'dropoff'
    );
    setLaterMode(pickupSessions.length > 0 && !pickupBlockedByInstagram ? 'pickup' : 'dropoff');
    setPickupSessionId(pickupSessions[0]?.id || '');
    setOrderError('');
    setClientSecret(null);
    setCheckoutOpen(true);
  };

  const handleSavePhone = async () => {
    if (!phoneDraft.trim()) return;
    setSavingPhone(true);
    try {
      const res = await fetch(secretShop.myPhone, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneDraft.trim() }),
      });
      const data = await res.json();
      if (data?.success) setNeedsPhone(false);
    } finally {
      setSavingPhone(false);
    }
  };

  const handleSavePassword = async () => {
    setPasswordError('');
    if (passwordDraft.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (passwordDraft !== passwordConfirmDraft) {
      setPasswordError("Passwords don't match");
      return;
    }
    setSavingPassword(true);
    try {
      const res = await fetch(secretShop.myPassword, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordDraft }),
      });
      const data = await res.json();
      if (data?.success) {
        setNeedsPassword(false);
        setPasswordDraft('');
        setPasswordConfirmDraft('');
      } else {
        setPasswordError(data?.error || 'Could not save your password.');
      }
    } catch {
      setPasswordError('Could not save your password.');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleArrived = async () => {
    setArriving(true);
    setArrivedNote('');
    try {
      const res = await fetch(secretShop.arrived, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (data?.success) {
        loadOrders();
      } else {
        setArrivedNote(data?.error || 'Could not send that.');
      }
    } catch (err) {
      setArrivedNote(err.message || 'Could not send that.');
    } finally {
      setArriving(false);
    }
  };

  const handleCreateOrder = async () => {
    setPlacingOrder(true);
    setOrderError('');
    try {
      const res = await fetch(secretShop.orders, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cartItems.map((i) => ({ secretProductId: i.product.id, quantity: i.quantity })),
          deliveryMode,
          ...(fulfilMode === 'pickup' ? { pickupSessionId } : {}),
          ...(fulfilMode === 'dropoff' ? { dropoffAddress: address } : {}),
          ...(deliveryMode === 'stall' && laterItems.length > 0 ? { laterMode: effectiveLaterMode } : {}),
        }),
      });
      const data = await res.json();
      if (!data?.success) {
        setOrderError(data?.error || 'Could not start this order.');
        setPlacingOrder(false);
        return;
      }
      if (data.data.free) {
        handlePaid();
        return;
      }
      const promise = await getStripePromise();
      setStripePromise(promise);
      setClientSecret(data.data.clientSecret);
    } catch (err) {
      setOrderError(err.message || 'Could not start this order.');
    } finally {
      setPlacingOrder(false);
    }
  };

  const handlePaid = () => {
    setCheckoutOpen(false);
    setClientSecret(null);
    setQuantities({});
    loadOrders();
    loadAccess(); // stock counts changed
  };

  const handleToggleOrderMessages = async (orderId) => {
    if (messagingOrderId === orderId) {
      setMessagingOrderId(null);
      return;
    }
    setMessagingOrderId(orderId);
    setMessageDraft('');
    setLoadingOrderMessages(true);
    try {
      const res = await fetch(API_ENDPOINTS.secretShop.orderMessages(orderId), { headers: getAuthHeaders() });
      const data = await res.json();
      setOrderMessages(data?.success && data.data ? data.data : []);
    } catch {
      setOrderMessages([]);
    } finally {
      setLoadingOrderMessages(false);
    }
  };

  const handleSendOrderMessage = async () => {
    if (!messagingOrderId || !messageDraft.trim()) return;
    setSendingMessage(true);
    try {
      const res = await fetch(API_ENDPOINTS.secretShop.orderMessages(messagingOrderId), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageDraft.trim() }),
      });
      const data = await res.json();
      if (data?.success) {
        setMessageDraft('');
        const refreshed = await fetch(API_ENDPOINTS.secretShop.orderMessages(messagingOrderId), { headers: getAuthHeaders() });
        const refreshedData = await refreshed.json();
        setOrderMessages(refreshedData?.success && refreshedData.data ? refreshedData.data : []);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) return <p style={{ color: T.inkLight }}>Loading…</p>;

  return (
    <div>
      {needsPhone && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: T.ink }}>📱 Add your phone number</div>
          <p style={{ fontSize: 12, color: T.inkLight, margin: '0 0 10px 0' }}>
            So we can reach you about your orders and pickups — it's also how you log back in.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...input, flex: 1 }} type="tel" autoComplete="tel" placeholder="Phone number" value={phoneDraft}
              onChange={(e) => setPhoneDraft(e.target.value)} />
            <button type="button" style={primaryBtn} onClick={handleSavePhone} disabled={savingPhone || !phoneDraft.trim()}>
              {savingPhone ? '…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {needsPassword && !needsPhone && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: T.ink }}>🔒 Create a password</div>
          <p style={{ fontSize: 12, color: T.inkLight, margin: '0 0 10px 0' }}>
            Right now your password is your phone number, which anyone who knows it could use. Set your own to keep your account yours.
          </p>
          <input style={{ ...input, marginBottom: 8 }} type="password" autoComplete="new-password" placeholder="New password (8+ characters)"
            value={passwordDraft} onChange={(e) => setPasswordDraft(e.target.value)} />
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...input, flex: 1 }} type="password" autoComplete="new-password" placeholder="Confirm password"
              value={passwordConfirmDraft} onChange={(e) => setPasswordConfirmDraft(e.target.value)} />
            <button type="button" style={primaryBtn} onClick={handleSavePassword}
              disabled={savingPassword || !passwordDraft || !passwordConfirmDraft}>
              {savingPassword ? '…' : 'Save'}
            </button>
          </div>
          {passwordError && <p style={{ fontSize: 12, color: T.red, margin: '8px 0 0 0' }}>{passwordError}</p>}
        </div>
      )}

      {liveSession && (
        <div style={{ ...card, border: `2px solid ${T.green}`, background: T.greenPale }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: T.green }}>🟢 We're at the stall right now</div>
          <div style={{ fontSize: 13, color: T.ink, marginTop: 4 }}>{formatSession(liveSession)}</div>
          <p style={{ fontSize: 12, color: T.inkLight, margin: '6px 0 0 0' }}>
            Buy below and choose "Buy at the stall now" — no fees, you walk away with it. Anything we don't have at the
            stall can go in the same order and be picked up or delivered later.
          </p>
        </div>
      )}

      {livePickup && pickupsHereNow.length > 0 && (
        <div style={{ ...card, border: `2px solid ${T.green}`, background: T.greenPale }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: T.green }}>📍 Your pickup is on now</div>
          <div style={{ fontSize: 13, color: T.ink, marginTop: 4 }}>
            {livePickup.hidden ? 'Where & when is in my Instagram DM.' : formatSession(livePickup)}
          </div>
          {pickupsHereNow.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {alreadyArrived ? (
                <p style={{ fontSize: 13, color: T.green, fontWeight: 600, margin: 0 }}>
                  ✅ We know you're here — we're coming with your order.
                </p>
              ) : (
                <button type="button" style={primaryBtn} onClick={handleArrived} disabled={arriving}>
                  {arriving ? 'Sending…' : `🙋 I'm here — collect my order (${pickupsHereNow.length})`}
                </button>
              )}
              {arrivedNote && <p style={{ fontSize: 12, color: T.red, margin: '8px 0 0 0' }}>{arrivedNote}</p>}
            </div>
          )}
        </div>
      )}

      {!liveSession && pickupSessions.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: T.ink }}>📍 Upcoming pickups</div>
          {pickupSessions.some((s) => s.hidden) && (
            <div style={{ fontSize: 12, color: T.inkLight }}>
              Once you've ordered, comment on any of my Instagram posts and I'll DM you where &amp; when.
            </div>
          )}
          {pickupSessions.map((s) => (
            <div key={s.id} style={{ fontSize: 13, color: T.ink, marginTop: 4 }}>
              {s.title ? <strong>{s.title}: </strong> : null}
              {formatSession(s)}
              <div style={{ fontSize: 12, color: T.inkLight }}>Pickup orders: {orderByText(s)}</div>
            </div>
          ))}
        </div>
      )}

      {products.length === 0 && (
        <div style={card}>
          <p style={{ color: T.inkLight, fontSize: 13, margin: 0 }}>
            {showAccessTabHint ? (
              <>Nothing unlocked yet — got a code? Enter it under the <strong>Access</strong> tab.</>
            ) : (
              'Nothing unlocked yet — reach out if you were expecting access.'
            )}
          </p>
        </div>
      )}

      {products.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: T.ink }}>Unlocked for you</div>
          <p style={{ fontSize: 12, color: T.inkLight, margin: '0 0 12px 0' }}>
            Set a quantity for anything you want, then check out once — mailbox delivery in 3–7 days
            (free at ${freeDeliveryThreshold}+, otherwise +${dropoffSurcharge.toFixed(2)})
            {pickupSessions.length > 0 && (
              <>, or pick up at one of our pickup times ({pickupFee > 0 ? `+$${pickupFee.toFixed(2)}` : 'free'})</>
            )}
            {liveSession && <>, or buy at the stall right now (no fee)</>}. ${discount5Threshold}+ gets 5% off,
            ${discount10Threshold}+ gets 10% off.
          </p>
          {visibleProducts.map((p) => {
            const soldOut = p.stockQuantity === 0;
            const atStockLimit = p.stockQuantity != null && (quantities[p.id] || 0) >= p.stockQuantity;
            return (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderTop: `1px solid ${T.border}`, padding: '12px 0', opacity: soldOut ? 0.6 : 1 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: T.ink }}>{p.name}</div>
                  {p.description && <div style={{ fontSize: 12, color: T.inkLight, marginTop: 2 }}>{p.description}</div>}
                  <div style={{ fontSize: 13, marginTop: 4, color: T.ink }}>${p.price.toFixed(2)} each</div>
                  {p.stallOnly && (
                    <div style={{ fontSize: 12, color: T.purple, marginTop: 2, fontWeight: 600 }}>🛍️ Stall only — collect in person</div>
                  )}
                  {p.notAtStall && (
                    <div style={{ fontSize: 12, color: T.inkLight, marginTop: 2, fontWeight: 600 }}>📦 Pickup &amp; delivery only — not at the stall</div>
                  )}
                  {soldOut ? (
                    <div style={{ fontSize: 12, color: T.red, marginTop: 2, fontWeight: 600 }}>Sold out</div>
                  ) : p.stockQuantity != null && p.stockQuantity <= 5 ? (
                    <div style={{ fontSize: 12, color: T.red, marginTop: 2 }}>Only {p.stockQuantity} left</div>
                  ) : null}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button type="button" style={stepBtn} onClick={() => setQuantity(p.id, (quantities[p.id] || 0) - 1)} disabled={!(quantities[p.id] > 0)}>−</button>
                  <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 600, color: T.ink }}>{quantities[p.id] || 0}</span>
                  <button type="button" style={stepBtn} onClick={() => setQuantity(p.id, (quantities[p.id] || 0) + 1)} disabled={soldOut || atStockLimit}>+</button>
                </div>
              </div>
            );
          })}
          {visibleProducts.length < products.length && (
            <p style={{ fontSize: 12, color: T.inkLight, marginTop: 12, marginBottom: 0 }}>
              Add a ${minQualifyingPrice}+ item to unlock the rest.
            </p>
          )}

          {cartItems.length > 0 && !checkoutOpen && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: T.ink }}>Subtotal: ${subtotal.toFixed(2)}</div>
              <button type="button" style={primaryBtn} onClick={openCheckout}>Checkout →</button>
            </div>
          )}

          {checkoutOpen && (
            <div style={{ marginTop: 14, background: T.grayPale, color: T.ink, borderRadius: 10, padding: 14 }}>
              {!clientSecret ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: T.ink }}>Your order</div>
                  {cartItems.map((i) => (
                    <div key={i.product.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, color: T.ink }}>
                      <span>{i.product.name} × {i.quantity}</span>
                      <span>${(i.product.price * i.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 10, marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, color: T.ink }}>How do you want it?</label>
                    {liveSession && (
                      <button type="button" style={optionBtn(deliveryMode === 'stall')} onClick={() => setDeliveryMode('stall')}>
                        🛍️ <strong>Buy at the stall now</strong> — no fee, handed to you on the spot
                        {cartNotAtStall.length > 0 ? " (anything we don't have here comes later)" : ''}
                      </button>
                    )}
                    {deliveryMode === 'stall' && laterItems.length > 0 && (
                      <div style={{ background: T.grayPale, borderRadius: 8, padding: '10px 12px', margin: '2px 0 8px', fontSize: 12, color: T.ink }}>
                        <div>
                          <strong>Handed to you now:</strong>{' '}
                          {nowItems.length > 0 ? nowItems.map((i) => `${i.product.name} × ${i.quantity}`).join(', ') : 'nothing'}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <strong>Not at the stall — we'll get it to you later</strong> (orders take 3–7 days):{' '}
                          {laterItems.map((i) => `${i.product.name} × ${i.quantity}`).join(', ')}
                        </div>
                        <div style={{ marginTop: 8 }}>
                          {pickupAvailable && (
                            <button type="button" style={optionBtn(effectiveLaterMode === 'pickup')} onClick={() => setLaterMode('pickup')}>
                              📍 <strong>Pick it up</strong> at a later pickup time{pickupFee > 0 ? ` (+$${pickupFee.toFixed(2)})` : ' — free'}
                            </button>
                          )}
                          {pickupSessions.length > 0 && pickupBlockedByInstagram && (
                            <div style={{ ...optionBtn(false), cursor: 'default', opacity: 0.75 }}>
                              📍 <strong>Pick it up later</strong> (free) — link your Instagram first (tap your name → My profile → Instagram).
                            </div>
                          )}
                          <button type="button" style={optionBtn(effectiveLaterMode === 'dropoff')} onClick={() => setLaterMode('dropoff')}>
                            📬 <strong>Deliver it</strong> to my mailbox
                            {fulfilSubtotal >= freeDeliveryThreshold ? (
                              <span style={{ color: T.green, fontWeight: 600 }}> — free!</span>
                            ) : (
                              <> (+${dropoffSurcharge.toFixed(2)} — free at ${freeDeliveryThreshold}+)</>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                    {pickupSessions.length === 0 && (
                      <div style={{ ...optionBtn(false), cursor: 'default', opacity: 0.55 }}>
                        📍 <strong>Pick up</strong> — no pickup times open for orders right now
                      </div>
                    )}
                    {pickupSessions.length > 0 && !pickupBlockedByInstagram && (
                      <button type="button" style={optionBtn(deliveryMode === 'pickup')} onClick={() => setDeliveryMode('pickup')}>
                        📍 <strong>Pick up</strong> at a set time & place{subtotal === 0 ? ' — free (test order)' : pickupFee > 0 ? ` (+$${pickupFee.toFixed(2)})` : ' — free'}
                      </button>
                    )}
                    {pickupSessions.length > 0 && pickupBlockedByInstagram && (
                      <div style={{ ...optionBtn(false), cursor: 'default', opacity: 0.75 }}>
                        📍 <strong>Pick up</strong> (free) — link your Instagram first. Tap your name at the top →{' '}
                        <strong>My profile</strong> → Instagram, and follow the 4 steps there.
                      </div>
                    )}
                    {!cartHasStallOnly && (
                      <button type="button" style={optionBtn(deliveryMode === 'dropoff')} onClick={() => setDeliveryMode('dropoff')}>
                        📬 <strong>Mailbox delivery</strong> in 3–7 days
                        {subtotal === 0 || subtotal >= freeDeliveryThreshold ? (
                          <span style={{ color: T.green, fontWeight: 600 }}> — free!</span>
                        ) : (
                          <> (+${dropoffSurcharge.toFixed(2)} — free at ${freeDeliveryThreshold}+)</>
                        )}
                      </button>
                    )}
                    {cartHasStallOnly && !liveSession && pickupSessions.length === 0 && (
                      <p style={{ fontSize: 12, color: T.red, margin: 0 }}>Stall-only items need a pickup time — none are scheduled right now.</p>
                    )}
                    {fulfilMode === 'pickup' && pickupSessions.length > 0 && (
                      <select
                        style={{ ...input, marginTop: 2, marginBottom: 8, background: '#fff' }}
                        value={pickupSessionId}
                        onChange={(e) => setPickupSessionId(e.target.value)}
                      >
                        {pickupSessions.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.title ? `${s.title} — ` : ''}
                            {formatSession(s)} ({orderByText(s)})
                          </option>
                        ))}
                      </select>
                    )}
                    {cartHasStallOnly && (deliveryMode === 'pickup' || deliveryMode === 'stall') && (
                      <div style={{ background: T.redPale, color: T.red, borderRadius: 8, padding: '8px 10px', fontSize: 12, marginTop: 4 }}>
                        ⚠️ Stall-only items must be collected during{' '}
                        {deliveryMode === 'stall'
                          ? 'this stall session'
                          : selectedPickupSession
                          ? formatSession(selectedPickupSession)
                          : 'your pickup session'}
                        . If they're not collected then, there are <strong>no refunds and no late pickups</strong>.
                      </div>
                    )}
                    {fulfilMode === 'pickup' && !(deliveryMode === 'pickup' && cartHasStallOnly) && (
                      <p style={{ fontSize: 12, color: T.inkLight, margin: '4px 0 0 0' }}>
                        Can't make it? Your order moves to the next pickup time and we'll let you know when & where.
                      </p>
                    )}
                    {discountRate > 0 && (
                      <p style={{ fontSize: 12, color: T.green, fontWeight: 600, margin: '4px 0 0 0' }}>
                        {Math.round(discountRate * 100)}% volume discount applied
                      </p>
                    )}
                  </div>
                  {fulfilMode === 'dropoff' && (
                  <div style={{ marginBottom: 10 }}>
                    <p style={{ fontSize: 12, color: T.inkLight, margin: '0 0 8px 0' }}>
                      {savedAddress
                        ? "We'll deliver to the mailbox/address saved on your profile — edit below if this order should go elsewhere."
                        : "Enter the mailbox/address we should deliver to."}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input style={{ ...input, color: T.ink, gridColumn: '1 / -1' }} placeholder="Street address" value={address.street}
                        onChange={(e) => setAddress({ ...address, street: e.target.value })} />
                      <input style={{ ...input, color: T.ink, gridColumn: '1 / -1' }} placeholder="Unit / apartment (optional)" value={address.apartment}
                        onChange={(e) => setAddress({ ...address, apartment: e.target.value })} />
                      <input style={{ ...input, color: T.ink }} placeholder="City" value={address.city}
                        onChange={(e) => setAddress({ ...address, city: e.target.value })} />
                      <input style={{ ...input, color: T.ink }} placeholder="State" value={address.state}
                        onChange={(e) => setAddress({ ...address, state: e.target.value })} />
                      <input style={{ ...input, color: T.ink }} placeholder="Postcode" value={address.postcode}
                        onChange={(e) => setAddress({ ...address, postcode: e.target.value })} />
                    </div>
                  </div>
                  )}
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: T.ink }}>Total: ${total.toFixed(2)}</div>
                  {needsQualifyingItem && (
                    <p style={{ color: T.red, fontSize: 13 }}>
                      {deliveryMode === 'stall'
                        ? `What we get to you later needs a $${minQualifyingPrice}+ item among it — or remove it and just buy what's here.`
                        : `Add a $${minQualifyingPrice}+ item for delivery or pickup${liveSession ? ' — or buy at the stall' : ''}.`}
                    </p>
                  )}
                  {orderError && <p style={{ color: T.red, fontSize: 13 }}>{orderError}</p>}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      style={primaryBtn}
                      onClick={handleCreateOrder}
                      disabled={
                        placingOrder ||
                        needsQualifyingItem ||
                        (fulfilMode === 'pickup' && !pickupSessionId) ||
                        (deliveryMode === 'dropoff' && cartHasStallOnly)
                      }
                    >
                      {placingOrder ? 'Preparing…' : 'Continue to payment'}
                    </button>
                    <button type="button" style={ghostBtn} onClick={() => setCheckoutOpen(false)}>Back</button>
                  </div>
                </>
              ) : stripePromise ? (
                <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                  <PayForm onPaid={handlePaid} onCancel={() => setCheckoutOpen(false)} />
                </Elements>
              ) : (
                <p style={{ color: T.red, fontSize: 13 }}>Payments aren't set up for this shop yet.</p>
              )}
            </div>
          )}
        </div>
      )}

      <div style={card}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: T.ink }}>Your orders</div>
        {myOrders.length === 0 ? (
          <p style={{ color: T.inkLight, fontSize: 13, margin: 0 }}>No orders yet.</p>
        ) : (
          myOrders.map((o) => (
            <div key={o.id} style={{ borderTop: `1px solid ${T.border}`, padding: '10px 0', fontSize: 13, color: T.ink }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{o.secretProduct.name} × {o.quantity}</div>
                  <div style={{ color: T.inkLight, marginTop: 2 }}>
                    {o.deliveryMode === 'stall' ? 'Bought at the stall' : o.deliveryMode === 'pickup' ? 'Pickup' : 'Drop-off'}
                    {` · $${o.amount.toFixed(2)} · ${o.paymentStatus}`}
                    {o.fulfilledAt ? (o.deliveryMode === 'dropoff' ? ' · Delivered' : ' · Collected') : ''}
                  </div>
                  {o.deliveryMode === 'pickup' && !o.fulfilledAt && (
                    <div style={{ color: T.ink, marginTop: 2, fontSize: 12 }}>
                      📍 {o.pickupSession
                        ? `${formatSession(o.pickupSession)}${o.pickupSession.hidden ? ` · ${IG_DETAILS_HINT}` : ''}`
                        : "Awaiting a pickup time — we'll let you know when & where."}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleOrderMessages(o.id)}
                  style={{ ...ghostBtn, padding: '6px 12px', fontSize: 12, whiteSpace: 'nowrap' }}
                >
                  {messagingOrderId === o.id ? 'Close' : 'Details'}
                </button>
              </div>
              {messagingOrderId === o.id && (
                <div style={{ marginTop: 10, background: T.grayPale, borderRadius: 10, padding: 12 }}>
                  <OrderDetails order={o} />
                  <div style={{ fontWeight: 700, fontSize: 12, color: T.ink, margin: '12px 0 8px 0' }}>💬 Messages</div>
                  <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 10 }}>
                    {loadingOrderMessages ? (
                      <div style={{ color: T.inkLight, fontSize: 12 }}>Loading…</div>
                    ) : orderMessages.length === 0 ? (
                      <div style={{ color: T.inkLight, fontSize: 12 }}>No messages yet.</div>
                    ) : (
                      orderMessages.map((m) => (
                        <div
                          key={m.id}
                          style={{
                            display: 'flex',
                            justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              maxWidth: '80%',
                              background: m.role === 'user' ? T.gradient : '#fff',
                              color: m.role === 'user' ? '#fff' : T.ink,
                              border: m.role === 'user' ? 'none' : `1px solid ${T.border}`,
                              borderRadius: 10,
                              padding: '7px 11px',
                              fontSize: 13,
                            }}
                          >
                            <div>{m.content}</div>
                            <div style={{ fontSize: 10, marginTop: 2, opacity: 0.75 }}>
                              {new Date(m.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      style={{ ...input, flex: 1 }}
                      placeholder="Type a message…"
                      value={messageDraft}
                      onChange={(e) => setMessageDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !sendingMessage) handleSendOrderMessage();
                      }}
                    />
                    <button type="button" style={primaryBtn} onClick={handleSendOrderMessage} disabled={sendingMessage || !messageDraft.trim()}>
                      {sendingMessage ? '…' : 'Send'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {videos.length > 0 && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: T.ink }}>Learn more</div>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
            {videos.map((v, i) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setOpenVideoIndex(i)}
                style={{
                  flex: '0 0 auto',
                  width: 120,
                  height: 80,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  background: '#000',
                  cursor: 'pointer',
                  padding: 0,
                  overflow: 'hidden',
                  position: 'relative',
                }}
                title={v.title || 'Video'}
              >
                {v.thumbnailUrl ? (
                  <img src={v.thumbnailUrl} alt={v.title || 'Video'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ color: '#fff', fontSize: 24 }}>▶</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {openVideoIndex !== null && (
        <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <button
            type="button"
            onClick={() => setOpenVideoIndex(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1001,
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
          <div
            ref={videoScrollerRef}
            style={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', width: '100%', height: '100%' }}
          >
            {videos.map((v) => (
              <div
                key={v.id}
                style={{ flex: '0 0 100%', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
              >
                <video src={v.videoUrl} controls style={{ maxWidth: '100%', maxHeight: '85%' }} />
                {v.title && <div style={{ color: '#fff', fontSize: 13, marginTop: 12, textAlign: 'center', padding: '0 20px' }}>{v.title}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SecretShopTab;
